/*
 * GTMResponseBuilder.js
 *
 * This file provides helper functions for building and pushing standardized Google Tag Manager (GTM) events
 * for the scheduler funnel, including entry, question, answer, and handoff stages. It ensures all required
 * analytics fields are present and consistent, and manages step index tracking for funnel analysis.
 *
 * Sample GTM event payload:
 * {
 *   question_id: 'service_type',
 *   question_text: 'What type of service do you need?',
 *   answer_value: 'plumbing',
 *   answer_label: 'Plumbing',
 *   value: 'plumbing',
 *   selectedLabel: 'Plumbing',
 *   questionText: 'What type of service do you need?',
 *   updatedAt: '2024-06-01T12:34:56.789Z',
 *   event: 'scheduler_step',
 *   gtm: {},
 *   pagePostType: 'frontpage',
 *   pagePostType2: 'single-page',
 *   pagePostAuthor: 'nearu',
 *   event_category: 'scheduler',
 *   scheduler_name: 'Plumbing - Booking Form',
 *   elementTag: 'section',
 *   entry_label: 'Plumbing',
 *   entry_key: 'plumbing',
 *   entry_type: 'modal',
 *   flow_id: '123e4567-e89b-12d3-a456-426614174000',
 *   funnel_stage: 'question',
 *   step_index: 1
 * }
 */

const STANDARD_KEYS = [
  'question_id',
  'question_text',
  'answer_value',
  'answer_label',
  'value',
  'selectedLabel',
  'questionText',
  'updatedAt',
  'event',
  'gtm',
  'pagePostType',
  'pagePostType2',
  'pagePostAuthor',
  'event_category',
  'scheduler_name',
  'elementTag',
  'entry_label',
  'entry_key',
  'entry_type',
  'flow_id',
  'funnel_stage',
  'step_index',
];

function toSnakeCase(str) {
  str = str === undefined || str === null ? '' : String(str);
  return str
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .toLowerCase();
}

function buildStandardPayload(src = {}) {
  const now = new Date().toISOString();
  const base = {
    question_id: '',
    question_text: '',
    answer_value: '',
    answer_label: '',
    value: '',
    selectedLabel: '',
    questionText: '',
    updatedAt: now,
    event: '',
    gtm: {},
    pagePostType: 'frontpage',
    pagePostType2: 'single-page',
    pagePostAuthor: 'nearu',
    event_category: 'scheduler',
    scheduler_name: '',
    elementTag: '',
    flow_id: '', // Add default for flow_id
  };
  // Normalize question/answer fields if present
  if (src.questionId) base.question_id = toSnakeCase(src.questionId);
  if (src.question_id) base.question_id = toSnakeCase(src.question_id);
  if (src.questionText) base.question_text = src.questionText;
  if (src.question_text) base.question_text = src.question_text;
  if (src.value) base.answer_value = toSnakeCase(src.value);
  if (src.answer_value) base.answer_value = toSnakeCase(src.answer_value);
  if (src.selectedLabel) base.answer_label = src.selectedLabel;
  if (src.answer_label) base.answer_label = src.answer_label;
  // Always set scheduler_name for GTM (no schedulerName)
  if (src.scheduler_name) base.scheduler_name = src.scheduler_name;
  // Preserve flow_id from source
  if (src.flow_id) base.flow_id = src.flow_id;
  const out = { ...base, ...src };
  // Always enforce scheduler category
  out.event_category = 'scheduler';
  // Guarantee only standard keys; fill missing with ''
  STANDARD_KEYS.forEach((k) => {
    if (out[k] === undefined || out[k] === null) out[k] = k === 'gtm' ? {} : '';
  });
  return out;
}

/**
 * Pushes a standardized event to the GTM dataLayer.
 * Used by all GTM event helpers.
 */
export function pushStandardEvent(payload) {
  const standardized = buildStandardPayload(payload);
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(standardized);
  }
  return standardized;
}

/**
 * Updates a local GTM response object (used for form state caching).
 * Not used for actual GTM pushes.
 */
export function updateGTMResponse(responseObj, payload) {
  if (!payload || !payload.questionId) return responseObj;
  const updated = { ...responseObj };
  updated[payload.questionId] = buildStandardPayload(payload);
  return updated;
}

/**
 * Pushes a scheduler step event to GTM (funnel_stage = 'question').
 * Should be called when a new question/screen is shown to the user.
 * Includes step_index for ordering and drop-off analysis.
 */
export function pushSchedulerStep({
  stepLabel,
  scheduler_name,
  flow_id,
  entry_label = '',
  entry_key = '',
  funnel_stage = 'question',
  step_index = undefined,
  question_id = '',
  question_text = '',
}) {
  return pushStandardEvent({
    event: 'scheduler_step',
    scheduler_name: scheduler_name || '',
    questionText: stepLabel || '',
    question_id: toSnakeCase(question_id || stepLabel),
    question_text: question_text || stepLabel || '',
    elementTag: 'section',
    value: '',
    selectedLabel: '',
    flow_id: flow_id || '',
    entry_label,
    entry_key,
    funnel_stage,
    step_index,
  });
}

/**
 * Pushes a selection or input event to GTM (funnel_stage = 'answer').
 * Should be called when a user answers a question (pill, dropdown, input, etc.).
 * Includes step_index for ordering and drop-off analysis.
 */
export function pushSelectionEvent(params = {}) {
  const {
    event = 'scheduler_field_interaction',
    value = '',
    selectedLabel = '',
    questionText = '',
    scheduler_name = '',
    elementTag = 'button',
    gtm = {},
    entry_label = '',
    entry_key = '',
    funnel_stage = 'answer',
    step_index = undefined,
    question_id = '',
    question_text = '',
    answer_value = '',
    answer_label = '',
    flow_id = '',
  } = params;
  return pushStandardEvent({
    event,
    value: value === null || value === undefined ? '' : value,
    selectedLabel,
    questionText,
    scheduler_name,
    elementTag,
    gtm: gtm && typeof gtm === 'object' ? gtm : {},
    entry_label,
    entry_key,
    funnel_stage,
    step_index,
    question_id: toSnakeCase(question_id || questionText),
    question_text: question_text || questionText || '',
    answer_value: toSnakeCase(answer_value || value),
    answer_label: answer_label || selectedLabel || '',
    flow_id,
  });
}

/**
 * Pushes an entry selection event to GTM (funnel_stage = 'entry').
 * Should be called when the user selects an entry point (e.g., form/modal open).
 */
export function pushSchedulerEntrySelect({
  entry_label = '',
  entry_key = '',
  entry_type = '',
  flow_id = '',
  funnel_stage = 'entry',
}) {
  return pushStandardEvent({
    event: 'scheduler_entry_select',
    entry_label,
    entry_key,
    entry_type,
    flow_id,
    funnel_stage,
  });
}

/**
 * Pushes a handoff event to GTM (funnel_stage = 'handoff').
 * Should be called when the user is handed off to ServiceTitan or another external system.
 */
export function pushSchedulerHandoffST({
  scheduler_name = '',
  entry_key = '',
  flow_id = '',
  questionText = '',
  entry_label = '',
  funnel_stage = 'handoff',
}) {
  return pushStandardEvent({
    event: 'scheduler_handoff_st',
    scheduler_name,
    entry_key,
    flow_id,
    questionText,
    entry_label,
    funnel_stage,
  });
}

// Step index tracker for funnel analysis
// Used to increment and reset step_index for question/answer events
let _stepIndex = 1;

/**
 * Resets the step index to 1. Call this when the funnel restarts.
 */
export function resetStepIndex() {
  _stepIndex = 1;
}

/**
 * Returns the current step index and increments it. Use for each question/answer event.
 */
export function getAndIncrementStepIndex() {
  return _stepIndex++;
}

/**
 * Sets the step index to a specific value (for advanced use).
 */
export function setStepIndex(val) {
  _stepIndex = Number(val) || 1;
}
